import { FormHelper } from '@presentation/components/FormHelper';
import { parseFlexibleDateToIso } from '@shared/utils/fmtDate';
import { ToastService } from '@presentation/components/Toast';
import { AppContext } from '@application/context/AppContext';
import { FactureAchatService } from '@domain/achats/FactureAchatService';
import { PrecompteISService } from '@domain/fiscalite/PrecompteISService';
import { numeroFactureDejaPris } from '@domain/paie/SalarieUniqueness';
import { uuid } from '@shared/utils/uuid';
import type { Facture, PrecompteType } from '@shared/types/Facture';
import type { ConditionsReglement } from '@shared/types/Tiers';
import { loadRegister } from '@presentation/shell/localCrud';
import { compteOhada } from '@shared/validation/identifiantsGabon';
import { loadBaremeTva } from '@application/comptabilite/BaremeTvaStore';
import { z } from 'zod';
import {
  compteChargeOptions,
  DEVISE_OPTIONS,
  factureLineRow,
  lierBaremeAuxFactures,
  factureTotalsBar,
  PRECOMPTE_OPTIONS,
  wireLiveTotals,
} from './factureFormShared';

/** Les champs date affichent JJ/MM/AAAA ; les schémas attendent AAAA-MM-JJ. */
function dateIso(v: FormDataEntryValue | null): string {
  const raw = typeof v === 'string' ? v.trim() : '';
  return parseFlexibleDateToIso(raw) ?? raw;
}

interface TiersLocal {
  id: string;
  code: string;
  type: 'client' | 'fournisseur' | 'mixte';
  raison_sociale: string;
  conditions_reglement?: ConditionsReglement;
}

const HeaderSchema = z.object({
  numero: z.string().min(1, 'Numéro requis').max(50),
  date_facture: z.string().min(1),
  date_echeance: z.string().min(1),
  fournisseur_id: z.string().min(1, 'Fournisseur requis'),
  fournisseur_compte: compteOhada,
  objet: z.string().optional(),
  devise: z.enum(['XAF', 'EUR', 'USD']).optional(),
  axe_cdc: z.string().optional(),
  precompte_type: z
    .enum(['', 'loyer', 'honoraires_nr', 'honoraires_res', 'prestations'])
    .optional(),
});

const LigneSchema = z.object({
  description: z.string().min(1),
  quantite: z.number().positive(),
  prix_unitaire: z.number().nonnegative(),
  taux_tva: z.number().min(0).max(1),
  compte_charge: compteOhada,
  axe_cdc: z.string().optional(),
});

function echeanceFromConditions(base: string, cond?: ConditionsReglement): string {
  const days = cond === 'comptant' ? 0 : cond === '60j' ? 60 : cond === '90j' ? 90 : 30;
  const d = new Date(base);
  if (Number.isNaN(d.getTime())) return base;
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

export class FactureFournisseurFormView {
  static render(container: HTMLElement, onSaved: () => void, onCancel = onSaved): void {
    void FactureFournisseurFormView.renderAsync(container, onSaved, onCancel);
  }

  private static async renderAsync(
    container: HTMLElement,
    onSaved: () => void,
    onCancel: () => void,
  ): Promise<void> {
    const ctx = AppContext.getInstance();
    const tid = ctx.tenantContext.tenantId();
    const allTiers = await loadRegister<TiersLocal>(tid, 'tiers');
    const bareme = await loadBaremeTva(tid);
    const tiers = allTiers.filter((t) => t.type === 'fournisseur' || t.type === 'mixte');
    const today = new Date().toISOString().slice(0, 10);
    const echeance = new Date(Date.now() + 30 * 24 * 3600 * 1000).toISOString().slice(0, 10);
    const tiersOptions =
      tiers.length > 0
        ? tiers.map((t) => ({ value: t.code || t.id, label: `${t.code} — ${t.raison_sociale}` }))
        : [{ value: '', label: 'Aucun fournisseur — créez-en dans Tiers' }];
    const compteOpts = compteChargeOptions();

    container.innerHTML = `
      <h2 style="margin-top:0;">Nouvelle facture fournisseur</h2>
      <form id="fa-form" style="max-width:960px;background:white;padding:24px;border:1px solid #e1e4e8;border-radius:8px;">
        <div style="display:grid;grid-template-columns:1fr 1fr 1fr;gap:12px;">
          ${FormHelper.field('numero', 'N° interne', 'text', { required: true, placeholder: 'FA-2026-001' })}
          ${FormHelper.field('date_facture', 'Date facture', 'date', { required: true, value: today })}
          ${FormHelper.field('date_echeance', 'Échéance', 'date', { required: true, value: echeance })}
        </div>
        <div style="display:grid;grid-template-columns:1fr 1fr;gap:12px;">
          ${FormHelper.select('fournisseur_id', 'Fournisseur', tiersOptions, { required: true })}
          ${FormHelper.field('fournisseur_compte', 'Compte fournisseur (401xxx)', 'text', { required: true, placeholder: '401001', value: '401001' })}
        </div>
        ${FormHelper.field('objet', 'Objet / libellé', 'text', { placeholder: 'Ex. Loyer bureau mars 2026' })}
        <div style="display:grid;grid-template-columns:1fr 1fr 1fr;gap:12px;">
          ${FormHelper.select('devise', 'Devise', DEVISE_OPTIONS, { value: 'XAF' })}
          ${FormHelper.select('precompte_type', 'Précompte IS', PRECOMPTE_OPTIONS, { value: '' })}
          ${FormHelper.field('axe_cdc', 'Axe CDC (en-tête)', 'text', { placeholder: 'Centre de coût global' })}
        </div>
        <hr style="border:0;border-top:1px solid #e1e4e8;margin:16px 0;" />
        <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:8px;">
          <h3 style="margin:0;font-size:1rem;">Lignes de facture</h3>
          <button type="button" id="add-line" class="btn btn-secondary" style="padding:6px 12px;">+ Ajouter une ligne</button>
        </div>
        <div id="fa-lignes"></div>
        <p id="fa-tva-status" role="status" style="font-size:0.8rem;color:#586069;margin:8px 0 0;"></p>
        ${factureTotalsBar({ ht: 'fa-total-ht', tva: 'fa-total-tva', ttc: 'fa-total-ttc' })}
        <div style="display:flex;justify-content:flex-end;gap:8px;margin-top:16px;">
          <button type="button" id="cancel" style="padding:8px 16px;background:white;color:#586069;border:1px solid #e1e4e8;border-radius:4px;cursor:pointer;">Annuler</button>
          <button type="submit" style="padding:8px 16px;background:#0a4d7e;color:white;border:none;border-radius:4px;font-weight:600;cursor:pointer;">Comptabiliser</button>
        </div>
      </form>
    `;

    const form = container.querySelector<HTMLFormElement>('#fa-form')!;
    const lignesBox = form.querySelector<HTMLElement>('#fa-lignes')!;
    let lineCount = 0;
    const liaison = lierBaremeAuxFactures({
      form,
      lignesBox,
      lineClass: 'fa-line',
      dateFieldName: 'date_facture',
      bareme,
      statusEl: form.querySelector<HTMLElement>('#fa-tva-status'),
      aujourdhui: today,
    });

    const addLine = (): void => {
      const idx = lineCount++;
      const row = document.createElement('div');
      const tva = liaison.courant();
      row.innerHTML = factureLineRow(idx, 'fa-line', compteOpts, '622', tva.options, tva.defaut);
      lignesBox.appendChild(row.firstElementChild!);
    };

    addLine();
    wireLiveTotals(form, lignesBox, 'fa-line', {
      ht: 'fa-total-ht',
      tva: 'fa-total-tva',
      ttc: 'fa-total-ttc',
    });

    form.querySelector('#add-line')?.addEventListener('click', () => addLine());
    lignesBox.addEventListener('click', (e) => {
      const btn = (e.target as HTMLElement).closest<HTMLButtonElement>('[data-remove]');
      if (!btn) return;
      if (lignesBox.querySelectorAll('.fa-line').length <= 1) {
        ToastService.warning('Au moins une ligne est requise');
        return;
      }
      btn.closest('.fa-line')?.remove();
      form.dispatchEvent(new Event('input', { bubbles: true }));
    });

    const fournisseurSelect = form.querySelector<HTMLSelectElement>('[name="fournisseur_id"]');
    const dateFactureInput = form.querySelector<HTMLInputElement>('[name="date_facture"]');
    const echeanceInput = form.querySelector<HTMLInputElement>('[name="date_echeance"]');
    const syncEcheance = (): void => {
      const code = fournisseurSelect?.value;
      const tier = tiers.find((t) => (t.code || t.id) === code);
      const base = dateFactureInput?.value || today;
      if (echeanceInput && tier?.conditions_reglement) {
        echeanceInput.value = echeanceFromConditions(base, tier.conditions_reglement);
      }
    };
    fournisseurSelect?.addEventListener('change', syncEcheance);
    dateFactureInput?.addEventListener('change', syncEcheance);

    container.querySelector('#cancel')?.addEventListener('click', () => onCancel());

    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      FormHelper.clearFieldErrors(form);
      const fd = new FormData(form);
      const header = HeaderSchema.safeParse({
        numero: fd.get('numero'),
        date_facture: dateIso(fd.get('date_facture')),
        date_echeance: dateIso(fd.get('date_echeance')),
        fournisseur_id: fd.get('fournisseur_id'),
        fournisseur_compte: fd.get('fournisseur_compte'),
        objet: fd.get('objet') || undefined,
        devise: fd.get('devise') || 'XAF',
        axe_cdc: fd.get('axe_cdc') || undefined,
        precompte_type: fd.get('precompte_type') || '',
      });
      if (!header.success) {
        ToastService.error(header.error.issues[0]?.message ?? 'Saisie invalide');
        return;
      }
      const headerAxe = header.data.axe_cdc?.trim() || undefined;
      const lignesRaw = Array.from(lignesBox.querySelectorAll<HTMLElement>('.fa-line')).map(
        (row) => {
          const idx = row.dataset.idx ?? '0';
          const lineAxe = String(fd.get(`axe_${idx}`) ?? '').trim() || headerAxe;
          return {
            description: String(fd.get(`desc_${idx}`) ?? ''),
            quantite: Number(fd.get(`qty_${idx}`) ?? 0),
            prix_unitaire: Number(fd.get(`pu_${idx}`) ?? 0),
            taux_tva: Number(fd.get(`tva_${idx}`) ?? 0),
            compte_charge: String(fd.get(`cpt_${idx}`) ?? ''),
            ...(lineAxe ? { axe_cdc: lineAxe } : {}),
          };
        },
      );
      const lignesParsed = z.array(LigneSchema).min(1).safeParse(lignesRaw);
      if (!lignesParsed.success) {
        ToastService.error(lignesParsed.error.issues[0]?.message ?? 'Lignes invalides');
        return;
      }
      if (header.data.date_echeance < header.data.date_facture) {
        ToastService.error('L’échéance précède la date de facture');
        return;
      }
      const existantes = await ctx.factures.list();
      if (numeroFactureDejaPris(existantes, header.data.numero)) {
        FormHelper.markFieldError(form, 'numero', 'Numéro déjà utilisé');
        ToastService.error('Numéro déjà utilisé');
        return;
      }
      const precompteType = (header.data.precompte_type || '') as PrecompteType;
      const optionsPrecompte = { date: header.data.date_facture };
      const precompteTaux = PrecompteISService.tauxFor(precompteType, optionsPrecompte);
      const calcul = FactureAchatService.calculer({
        fournisseur_id: header.data.fournisseur_id,
        lignes: lignesParsed.data,
        ...(precompteTaux > 0 ? { precompte_taux: precompteTaux } : {}),
      });
      const precompteInfo =
        precompteTaux > 0
          ? PrecompteISService.calculer(calcul.total_ht, precompteType, optionsPrecompte)
          : { taux: 0, montant: 0 };
      const achat = {
        id: uuid('fa'),
        tenant_id: tid,
        numero: header.data.numero,
        fournisseur_id: header.data.fournisseur_id,
        date_facture: header.data.date_facture,
        date_reception: header.data.date_facture,
        date_echeance: header.data.date_echeance,
        ...calcul,
        statut: 'validee' as const,
        cree_le: new Date().toISOString(),
      };
      const persist: Facture = {
        id: achat.id,
        tenant_id: achat.tenant_id,
        numero: achat.numero,
        type: 'achat',
        client_id: header.data.fournisseur_id,
        date: header.data.date_facture,
        date_echeance: header.data.date_echeance,
        devise: header.data.devise ?? 'XAF',
        objet: header.data.objet,
        axe_cdc: headerAxe,
        precompte_type: precompteType || undefined,
        precompte_taux: precompteInfo.taux || undefined,
        precompte_montant: precompteInfo.montant || undefined,
        lignes: calcul.lignes.map((l) => ({
          description: l.description,
          quantite: l.quantite,
          prix_unitaire: l.prix_unitaire,
          taux_tva: l.taux_tva,
          compte_produit: l.compte_charge,
          montant_ht: l.montant_ht,
          montant_tva: l.montant_tva,
          montant_ttc: l.montant_ttc,
          axe_cdc: l.axe_cdc,
        })),
        total_ht: calcul.total_ht,
        total_tva: calcul.total_tva,
        total_ttc: calcul.total_ttc,
        statut: 'comptabilisee',
        cree_le: achat.cree_le,
      };
      await ctx.factures.add(persist);
      const ecritures = FactureAchatService.genererEcritures(achat, header.data.fournisseur_compte);
      for (const ecr of ecritures) {
        await ctx.ecritures.add({ ...ecr, tenant_id: tid });
      }
      ToastService.success(
        `Facture ${achat.numero} comptabilisée — TTC ${achat.total_ttc.toLocaleString('fr-FR')} FCFA (${lignesParsed.data.length} ligne(s))`,
      );
      onSaved();
    });
  }
}
